import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { NbDialogService, NbIconLibraries } from "@nebular/theme";
import { replace, remove, clone, find } from "lodash";

import { User } from "../../@core/data/users";
import { UserService } from "../../@core/services/user.service";
import { convertTimestamp } from "../../@core/utils/convertTimestamps.service";

import Like from "../../models/like.model";
import Course from "../../models/course.model";
import LectureContent from "../../models/lecture-content.model";
import QnA, { QnAReply } from "../../models/qna.model";
import Quiz from "../../models/quiz.model";
import Comment from "../../models/comment.model";

import { LikeService } from "../../services/like.service";
import ReportSpam from "../../models/report-spam.model";
import Feedback from "../../models/feedback.model";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import Exercise from "../../models/exercise.model";
import { DialogAuthLoginComponent } from "../dialog-auth-login/dialog-auth-login.component";
import Announcement from "../../models/announcement.model";

@Component({
  selector: "ngx-view-like",
  styleUrls: ["./view-like.component.scss"],
  templateUrl: "./view-like.component.html",
})
export class ViewLikeComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  @Input() course? = new Course();
  @Input() lecture? = new LectureContent();
  @Input() exercise? = new Exercise();
  @Input() quiz? = new Quiz();
  @Input() comment? = new Comment();
  @Input() qna? = new QnA();
  @Input() qnaReply? = new QnAReply();
  @Input() reported? = new ReportSpam();
  @Input() feedback? = new Feedback();
  @Input() announcement? = new Announcement();

  @Output() onClose: EventEmitter<any> = new EventEmitter();

  noLiked: number = 0;

  submitted = false;
  loading = false;

  liked?: Like = new Like();
  uid = "anonymous";
  isLogin = false;

  constructor(
    public userService: UserService,
    private likeService: LikeService,
    private iconLibraries: NbIconLibraries,
    private dialogService: NbDialogService
  ) {
    this.iconLibraries.registerSvgPack("liked-icon", {
      like: '<svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="thumbs-up" class="svg-inline--fa fa-thumbs-up fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M466.27 286.69C475.04 271.84 480 256 480 236.85c0-44.015-37.218-85.58-85.82-85.58H357.7c4.92-12.81 8.85-28.13 8.85-46.54C366.55 31.936 328.86 0 271.28 0c-61.607 0-58.093 94.933-71.76 108.6-22.747 22.747-49.615 66.447-68.76 83.4H32c-17.673 0-32 14.327-32 32v240c0 17.673 14.327 32 32 32h64c14.893 0 27.408-10.174 30.978-23.95 44.509 1.001 75.06 39.94 177.802 39.94 7.22 0 15.22.01 22.22.01 77.117 0 111.986-39.423 112.94-95.33 13.319-18.425 20.299-43.122 17.34-66.99 9.854-18.452 13.664-40.343 8.99-62.99zm-61.75 53.83c12.56 21.13 1.26 49.41-13.94 57.57 7.7 48.78-17.608 65.9-53.12 65.9h-37.82c-71.639 0-118.029-37.82-171.64-37.82V240h10.92c28.36 0 67.98-70.89 94.54-97.46 28.36-28.36 18.91-75.63 37.82-94.54 47.27 0 47.27 32.98 47.27 56.73 0 39.17-28.36 56.72-28.36 94.54h103.99c21.11 0 37.73 18.91 37.82 37.82.09 18.9-12.82 37.81-22.27 37.81 13.489 14.555 16.371 45.236-5.21 65.62zM88 432c0 13.255-10.745 24-24 24s-24-10.745-24-24 10.745-24 24-24 24 10.745 24 24z"></path></svg>',
      liked:
        '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="thumbs-up" class="svg-inline--fa fa-thumbs-up fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M104 224H24c-13.255 0-24 10.745-24 24v240c0 13.255 10.745 24 24 24h80c13.255 0 24-10.745 24-24V248c0-13.255-10.745-24-24-24zM64 472c-13.255 0-24-10.745-24-24s10.745-24 24-24 24 10.745 24 24-10.745 24-24 24zM384 81.452c0 42.416-25.97 66.208-33.277 94.548h101.723c33.397 0 59.397 27.746 59.553 58.098.084 17.938-7.546 37.249-19.439 49.197l-.11.11c9.836 23.337 8.237 56.037-9.308 79.469 8.681 25.895-.069 57.704-16.382 74.757 4.298 17.598 2.244 32.575-6.148 44.632C440.202 511.587 389.616 512 346.839 512l-2.845-.001c-48.287-.017-87.806-17.598-119.56-31.725-15.957-7.099-36.821-15.887-52.651-16.178-6.54-.12-11.783-5.457-11.783-11.998v-213.77c0-3.2 1.282-6.271 3.558-8.521 39.614-39.144 56.648-80.587 89.117-113.111 14.804-14.832 20.188-37.236 25.393-58.902C282.515 39.293 291.817 0 312 0c24 0 72 8 72 81.452z"></path></svg>',
    });
  }

  ngOnInit() {
    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User) => {
        if (user && user.uid != this.uid) {
          this.uid = user.uid;
          this.isLogin = true;
        } else {
          this.uid = "anonymous";
          this.isLogin = false;
        }
        this.getMyLiked();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  like() {
    if (this.isLogin) {
      this.liked.mode = this.liked.mode ? this.liked.mode : "";
      this.liked.refId = this.liked.refId ? this.liked.refId : "";
      this.liked.refTitle = this.liked.refTitle ? this.liked.refTitle : "";
      this.liked.refDescription = this.liked.refDescription
        ? this.liked.refDescription
        : "";
      this.liked.refUid = this.liked.refUid ? this.liked.refUid : "";
      this.liked.refUrl = this.liked.refUrl ? this.liked.refUrl : "";

      this.liked.value = this.liked.value ? this.liked.value : false;

      if (this.liked && this.liked.id && this.liked.id != "") {
        this.updateLike();
      } else {
        this.createLike();
      }
    }
    // else {
    //   this.dialogService
    //     .open(DialogAuthLoginComponent, {
    //       context: {
    //         mode: "header",
    //       },
    //       closeOnBackdropClick: false,
    //     })
    //     .onClose.subscribe((user) => {
    //       if (user) {
    //         console.log("DialogAuthLoginComponent/login:", user);
    //         this.uid = user.uid;
    //         this.isLogin = true;
    //       }
    //     });
    // }
  }

  updateLike() {
    this.loading = true;
    this.submitted = true;

    this.liked.value = this.liked.value ? false : true;
    this.likeService
      .update(this.liked)
      .then((value) => {
        let i = this.liked.value ? 1 : -1;
        this.calculateNoLiked(i);

        this.loading = false;
        this.submitted = false;
      })
      .catch((err) => {
        this.loading = false;
        this.submitted = false;
      });
  }

  createLike() {
    this.initLike();

    this.loading = true;
    this.submitted = true;

    this.liked.value = true;

    this.likeService
      .create(this.liked)
      .then((value) => {
        this.liked = clone(value);
        this.calculateNoLiked(1);

        this.loading = false;
        this.submitted = false;
      })
      .catch((err) => {
        this.loading = false;
        this.submitted = false;
      });
  }

  private initLike() {
    this.noLiked = 0;
    this.liked = new Like();
    this.liked.mode = "";
    this.liked.refId = "";
    this.liked.refUrl = "";
    this.liked.value = false;
    if (this.course && this.course.id) {
      this.liked.mode = "courses";
      this.liked.refId = this.course.id;
      this.liked.refTitle = this.course.title;
      this.liked.refUid = this.course.createdBy;
      this.liked.refAuthor = this.course.author;
      this.liked.refUrl = `/course/${this.course.id}`;

      this.noLiked = this.course.noLiked ? this.course.noLiked : 0;
    } else if (this.lecture && this.lecture.id) {
      this.liked.mode = "lecture-contents";
      this.liked.refId = this.lecture.id;
      this.liked.refTitle = this.lecture.title;
      this.liked.refUid = this.lecture.createdBy;
      this.liked.refAuthor = this.lecture.author;
      this.liked.refUrl = `/lecture/${this.lecture.id}`;

      this.noLiked = this.lecture.noLiked ? this.lecture.noLiked : 0;
    } else if (this.exercise && this.exercise.id) {
      this.liked.mode = "exercises";
      this.liked.refId = this.exercise.id;
      this.liked.refTitle = this.exercise.title;
      this.liked.refUid = this.exercise.createdBy;
      this.liked.refAuthor = this.exercise.author;
      this.liked.refUrl = `/exercise/${this.exercise.id}`;

      this.noLiked = this.exercise.noLiked ? this.exercise.noLiked : 0;
    } else if (this.qna && this.qna.id) {
      this.liked.mode = "qnas";
      this.liked.refId = this.qna.id;
      this.liked.refTitle = this.qna.title;
      this.liked.refUid = this.qna.createdBy;
      this.liked.refAuthor = this.qna.author;
      this.liked.refUrl = `/qna/${this.qna.id}`;

      this.noLiked = this.qna.noLiked ? this.qna.noLiked : 0;
    } else if (this.qnaReply && this.qnaReply.qnaId && this.qnaReply.id) {
      this.liked.mode = `qnas/${this.qnaReply.qnaId}/replies`;
      this.liked.refId = this.qnaReply.id;
      this.liked.refTitle = this.qnaReply.content;
      this.liked.refUid = this.qnaReply.createdBy;
      this.liked.refAuthor = this.qnaReply.author;
      this.liked.refDescription = this.qnaReply.qnaTitle;
      this.liked.refUrl = `/qna/${this.qnaReply.qnaId}`;

      this.noLiked = this.qnaReply.noLiked ? this.qnaReply.noLiked : 0;
    } else if (this.quiz && this.quiz.id) {
      this.liked.mode = "quizzes";
      this.liked.refId = this.quiz.id;
      this.liked.refTitle = this.quiz.title;
      this.liked.refUid = this.quiz.createdBy;
      this.liked.refAuthor = this.quiz.author;
      this.liked.refUrl = `/quiz/${this.quiz.id}`;

      this.noLiked = this.quiz.noLiked ? this.quiz.noLiked : 0;
    } else if (this.comment && this.comment.id) {
      this.liked.mode = "comments";
      this.liked.refId = this.comment.id;
      this.liked.refTitle = this.comment.content;
      this.liked.refUid = this.comment.createdBy;
      this.liked.refAuthor = this.comment.author;
      this.liked.refDescription = this.comment.refTitle;
      this.liked.refUrl = this.comment.refUrl;

      this.noLiked = this.comment.noLiked ? this.comment.noLiked : 0;
    } else if (this.reported && this.reported.id) {
      this.liked.mode = "report-spams";
      this.liked.refId = this.reported.id;
      this.liked.refTitle = this.reported.refTitle;
      this.liked.refUid = this.reported.createdBy;
      this.liked.refAuthor = this.reported.author;
      this.liked.refUrl = `/reported/${this.reported.id}`;

      this.noLiked = this.reported.noLiked ? this.reported.noLiked : 0;
    } else if (this.feedback && this.feedback.id) {
      this.liked.mode = "feedbacks";
      this.liked.refId = this.feedback.id;
      this.liked.refTitle = this.feedback.title;
      this.liked.refUid = this.feedback.createdBy;
      this.liked.refAuthor = this.feedback.author;
      this.liked.refUrl = `/feedback/${this.feedback.id}`;

      this.noLiked = this.feedback.noLiked ? this.feedback.noLiked : 0;
    } else if (this.announcement && this.announcement.id) {
      this.liked.mode = "announcements";
      this.liked.refId = this.announcement.id;
      this.liked.refTitle = this.announcement.title;
      this.liked.refUid = this.announcement.createdBy;
      this.liked.refAuthor = this.announcement.author;
      this.liked.refUrl = `/announcement/${this.announcement.id}`;

      this.noLiked = this.announcement.noLiked ? this.announcement.noLiked : 0;
    }
  }

  private getMyLiked(): void {
    this.initLike();

    if (this.liked.mode != "" && this.liked.refId != "" && this.uid != "") {
      this.loading = true;

      this.likeService
        .getMyLike(this.liked.mode, this.liked.refId, this.uid)
        .then((querySnapshot) => {
          if (querySnapshot.docs.length > 0) {
            querySnapshot.forEach((doc) => {
              let data = new Like();
              data = doc.data();
              data.id = doc.id;
              data.createdOn = convertTimestamp(doc.data().createdOn);
              this.liked.id = data.id;
              this.liked.value = data.value;
            });
          }

          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
        });
    }
  }

  private calculateNoLiked(value) {
    this.noLiked += value;

    if (this.course && this.course.id) {
      this.course.noLiked += value;
    } else if (this.lecture && this.lecture.id) {
      this.lecture.noLiked += value;
    } else if (this.exercise && this.exercise.id) {
      this.exercise.noLiked += value;
    } else if (this.quiz && this.quiz.id) {
      this.quiz.noLiked += value;
    } else if (this.reported && this.reported.id) {
      this.reported.noLiked += value;
    } else if (this.feedback && this.feedback.id) {
      this.feedback.noLiked += value;
    } else if (this.announcement && this.announcement.id) {
      this.announcement.noLiked += value;
    }
  }
}
